import { useEffect, useState } from "react";
import Modal from "./Modal";
import type { Deal, TeamMember } from "../../../lib/types";
import { supabase } from "../../../lib/supabase";
import { schedulePreInspection } from "../../../lib/dispatchActions";
import { useAuth } from "../../../context/AuthContext";

interface Props {
  open: boolean;
  deal: Deal;
  onClose: () => void;
  onCompleted: () => void;
}

export default function SchedulePreModal({
  open,
  deal,
  onClose,
  onCompleted,
}: Props) {
  const { member } = useAuth();
  const [rsList, setRsList] = useState<TeamMember[]>([]);
  const [rsId, setRsId] = useState<string>("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    supabase
      .from("team_roster")
      .select("*")
      .in("role", ["roof_specialist", "roof_team_manager"])
      .eq("is_active", true)
      .order("full_name")
      .then(({ data }) => {
        const list = (data ?? []) as TeamMember[];
        setRsList(list);
        if (list.length && !rsId) setRsId(list[0].id);
      });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const isWeekend = (() => {
    if (!date) return false;
    const d = new Date(date + "T12:00:00");
    const day = d.getDay();
    return day === 0 || day === 6;
  })();

  async function submit() {
    setErr(null);
    if (!rsId) return setErr("Pick a Roof Specialist.");
    if (!date) return setErr("Pick a date.");
    if (isWeekend) return setErr("Pre-inspections cannot be scheduled on weekends.");
    if (!time) return setErr("Pick a time.");
    setSubmitting(true);
    try {
      await schedulePreInspection(
        deal,
        { rsId, date, time },
        member?.id ?? null,
      );
      onCompleted();
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      title="Schedule pre-inspection"
      subtitle={`${deal.homeowner_name ?? "(no name)"} · ${deal.property_street ?? ""}`}
      onClose={onClose}
      footer={
        <>
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 rounded text-sm text-slate-600 hover:text-slate-800"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className="px-4 py-1.5 rounded bg-bardin-orange text-white text-sm disabled:opacity-60"
          >
            {submitting ? "Saving…" : "Schedule PRE"}
          </button>
        </>
      }
    >
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Roof Specialist
        </label>
        <select
          value={rsId}
          onChange={(e) => setRsId(e.target.value)}
          className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
        >
          {rsList.map((r) => (
            <option key={r.id} value={r.id}>
              {r.full_name}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Date
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
          />
          {isWeekend && (
            <p className="text-xs text-red-600 mt-1">No weekend scheduling — pick a weekday.</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Time
          </label>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
          />
        </div>
      </div>

      <p className="text-xs text-slate-500">
        PRE SLA = 3 days from RS assignment. Homeowner gets confirmation text.
      </p>

      {err && (
        <div className="bg-red-50 text-red-700 text-sm p-2 rounded">{err}</div>
      )}
    </Modal>
  );
}
